export const loadPriceProducts = async ({ request, accountId }) => {
  const data = await request(`/api/v1/ozon-prices/${accountId}/products`);
  return data.items || [];
};

export const fetchPrices = async ({ request, accountId, offerIds }) => {
  const data = await request(`/api/v1/ozon-prices/${accountId}/fetch-prices`, {
    method: 'POST',
    body: JSON.stringify({ offer_ids: offerIds }),
  });
  return data.items || [];
};

export const updatePrices = async ({ request, accountId, prices }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/update-prices`, {
    method: 'POST',
    body: JSON.stringify({ prices }),
  });
};

export const checkPromotions = async ({ request, accountId, productIds }) => {
  const data = await request(`/api/v1/ozon-prices/${accountId}/promotions`, {
    method: 'POST',
    body: JSON.stringify({ product_ids: productIds }),
  });
  return data.items || [];
};

export const listPromotionActions = async ({ request, accountId }) => {
  const data = await request(`/api/v1/ozon-prices/${accountId}/promotions/actions`);
  return data.items || [];
};

export const getPromotionCandidates = async ({ request, accountId, actionId, limit = 100, lastId }) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (lastId) params.set('last_id', lastId);
  const data = await request(`/api/v1/ozon-prices/${accountId}/promotions/${actionId}/candidates?${params.toString()}`);
  return { items: data.items || [], total: data.total || 0, lastId: data.last_id || '' };
};

export const activatePromotionProducts = async ({ request, accountId, actionId, products }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/${actionId}/activate`, {
    method: 'POST',
    body: JSON.stringify({ products }),
  });
};

export const deactivatePromotionProducts = async ({ request, accountId, actionId, productIds }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/${actionId}/deactivate`, {
    method: 'POST',
    body: JSON.stringify({ product_ids: productIds }),
  });
};

export const getPromotionAutomationConfig = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/automation/config`);
};

export const savePromotionAutomationConfig = async ({ request, accountId, config }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/automation/config`, {
    method: 'POST',
    body: JSON.stringify(config),
  });
};

export const runPromotionAutomationNow = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/automation/run`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
};

export const listPromotionAutomationLog = async ({ request, accountId, limit = 100 }) => {
  const data = await request(`/api/v1/ozon-prices/${accountId}/promotions/automation/log?limit=${limit}`);
  return data.items || [];
};

export const getPromotionsOverview = async ({ request, accountId }) => {
  const data = await request(`/api/v1/ozon-prices/${accountId}/promotions/overview`);
  return data.items || [];
};

export const savePromotionProductSetting = async ({
  request, accountId, productId, autoExitEnabled, autoJoinEnabled, maxDiscountPercent,
}) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/product-settings`, {
    method: 'POST',
    body: JSON.stringify({
      product_id: productId, auto_exit_enabled: autoExitEnabled, auto_join_enabled: autoJoinEnabled,
      max_discount_percent: maxDiscountPercent ?? null,
    }),
  });
};

export const runPromotionAutomationExitAll = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/automation/run-exit-all`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
};

export const runPromotionAutomationJoinAll = async ({ request, accountId, maxDiscountPercent }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/automation/run-join-all`, {
    method: 'POST',
    body: JSON.stringify({ max_discount_percent: maxDiscountPercent }),
  });
};

export const runPromotionAutomationJoinWithBoundedExit = async ({ request, accountId, maxDiscountPercent }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/automation/run-join-with-bounded-exit`, {
    method: 'POST',
    body: JSON.stringify({ max_discount_percent: maxDiscountPercent }),
  });
};

export const getRepricerConfig = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/repricer/config`);
};

export const saveRepricerConfig = async ({ request, accountId, config_json, is_active }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/repricer/config`, {
    method: 'POST',
    body: JSON.stringify({ config_json, is_active }),
  });
};

export const applyRepricerManual = async ({ request, accountId, slot_name }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/repricer/apply-now`, {
    method: 'POST',
    body: JSON.stringify({ slot_name }),
  });
};

export const getRepricerRuns = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/repricer/runs`);
};

export const getRepricerRunItems = async ({ request, accountId, runId }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/repricer/runs/${runId}/items`);
};

export const bulkChangePrice = async ({ request, accountId, prices }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/bulk-change`, {
    method: 'POST',
    body: JSON.stringify({ prices }),
  });
};

export const fetchParserCurrentPrices = async ({ request, accountId }) => {
  const data = await request(`/api/v1/parser/prices/current?account_id=${accountId}`);
  return Array.isArray(data) ? data : [];
};

export const createPriceSchedule = async ({ request, accountId, items, days }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/price-schedule`, {
    method: 'POST',
    body: JSON.stringify({ items, days }),
  });
};

export const listPriceSchedules = async ({ request, accountId, status = 'active' }) => {
  const data = await request(`/api/v1/ozon-prices/${accountId}/price-schedules?status=${status}`);
  return data.schedules || [];
};

export const cancelPriceSchedule = async ({ request, accountId, scheduleId }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/price-schedules/${scheduleId}`, {
    method: 'DELETE',
  });
};

export const listPriceChangeLog = async ({
  request, accountId, limit = 50, offset = 0, offerId, source, status,
}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (offerId) params.set('offer_id', offerId);
  if (source) params.set('source', source);
  if (status) params.set('status', status);
  const data = await request(`/api/v1/ozon-prices/${accountId}/price-change-log?${params.toString()}`);
  return { items: data.items || [], total: data.total || 0 };
};

// ── Vitrine Monitor ───────────────────────────────────────────────────────

export const vitrineGetSettings = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-vitrine/${accountId}/settings`);
};

export const vitrineSaveSettings = async ({ request, accountId, payload }) => {
  return await request(`/api/v1/ozon-vitrine/${accountId}/settings`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const vitrineRequestCheck = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-vitrine/${accountId}/check`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
};

export const vitrineManualCostPreview = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-vitrine/${accountId}/cost-preview/manual`);
};

export const vitrineAutoCostPreview = async ({ request, accountId, scheduleJson }) => {
  return await request(`/api/v1/ozon-vitrine/${accountId}/cost-preview/auto`, {
    method: 'POST',
    body: JSON.stringify({ schedule_json: scheduleJson || null }),
  });
};

export const vitrineGetPricePivot = async ({ request, accountId, limit }) => {
  return await request(`/api/v1/ozon-vitrine/${accountId}/prices/pivot?limit=${limit || 5}`);
};

export const vitrinePollTask = async ({ request, accountId, taskId }) => {
  return await request(`/api/v1/ozon-vitrine/${accountId}/tasks/${taskId}/poll`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
};

export const vitrineListTasks = async ({ request, accountId }) => {
  const data = await request(`/api/v1/ozon-vitrine/${accountId}/tasks`);
  return data.tasks || [];
};

export const vitrineGetLatestPrices = async ({ request, accountId }) => {
  const data = await request(`/api/v1/ozon-vitrine/${accountId}/prices`);
  return data.items || [];
};

export const vitrineGetPriceHistory = async ({ request, accountId, offerId }) => {
  const data = await request(`/api/v1/ozon-vitrine/${accountId}/prices/${encodeURIComponent(offerId)}/history`);
  return data.history || [];
};

// ── MAP repricer (Соблюдение РРЦ) ───────────────────────────────────────────

export const mapRepricerListProducts = async ({ request, accountId }) => {
  const data = await request(`/api/v1/ozon-map-repricer/${accountId}/products`);
  return data.products || [];
};

export const mapRepricerGetMedianYield = async ({ request, accountId }) => {
  const data = await request(`/api/v1/ozon-map-repricer/${accountId}/median-yield`);
  return !!data.enabled;
};

export const mapRepricerSetMedianYield = async ({ request, accountId, enabled }) => {
  const data = await request(`/api/v1/ozon-map-repricer/${accountId}/median-yield`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
  return !!data.enabled;
};

export const mapRepricerSaveProduct = async ({ request, accountId, offerId, payload }) => {
  return await request(`/api/v1/ozon-map-repricer/${accountId}/products/${encodeURIComponent(offerId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const mapRepricerDeleteProduct = async ({ request, accountId, offerId }) => {
  return await request(`/api/v1/ozon-map-repricer/${accountId}/products/${encodeURIComponent(offerId)}`, {
    method: 'DELETE',
  });
};

export const mapRepricerCopyFromMonitoring = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-map-repricer/${accountId}/copy-from-monitoring`);
};

export const mapRepricerLivePrices = async ({ request, accountId, offerIds, priceBasis = 'with_card' }) => {
  const data = await request(`/api/v1/ozon-map-repricer/${accountId}/live-prices`, {
    method: 'POST',
    body: JSON.stringify({ offer_ids: offerIds, price_basis: priceBasis }),
  });
  return data.items || {};
};

export const mapRepricerListRuns = async ({ request, accountId, limit = 50 }) => {
  const data = await request(`/api/v1/ozon-map-repricer/${accountId}/runs?limit=${limit}`);
  return data.runs || [];
};

export const mapRepricerRunItems = async ({ request, accountId, runId }) => {
  const data = await request(`/api/v1/ozon-map-repricer/${accountId}/runs/${runId}/items`);
  return data.items || [];
};

// ── Profitability ──────────────────────────────────────────────────────────

export const loadProfitabilityProducts = async ({ request, accountId }) => {
  const data = await request(`/api/v1/ozon-prices/${accountId}/profitability/products`);
  return data.items || [];
};

export const loadProfitabilityCogs = async ({ request, accountId }) => {
  const data = await request(`/api/v1/ozon-prices/${accountId}/profitability/cogs`);
  return data.items || [];
};

export const saveProfitabilityCogs = async ({ request, accountId, items }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/profitability/cogs`, {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
};

export const loadProfitabilityDrr = async ({ request, accountId, days = 30 }) => {
  const data = await request(`/api/v1/ozon-prices/${accountId}/profitability/drr?days=${days}`);
  return data.items || {};
};


export const getPromotionCatalog = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/catalog`);
};

export const syncPromotionState = async ({ request, accountId }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/state/sync`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
};

export const setPromotionActionPrice = async ({ request, accountId, actionId, items }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/manual/action-price`, {
    method: 'POST',
    body: JSON.stringify({ action_id: actionId, items }),
  });
};

export const exitPromotion = async ({ request, accountId, actionId, productIds }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/manual/exit`, {
    method: 'POST',
    body: JSON.stringify({ action_id: actionId, product_ids: productIds }),
  });
};

export const removeFromAutoAdd = async ({ request, accountId, actionId, autoAddDate, productIds }) => {
  return await request(`/api/v1/ozon-prices/${accountId}/promotions/manual/auto-add-remove`, {
    method: 'POST',
    body: JSON.stringify({ action_id: actionId, auto_add_date: autoAddDate, product_ids: productIds }),
  });
};
